import { useQuery } from '@tanstack/react-query'
import {
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  beneficiarioTaxPayerId?: string
  onClose(): void
}

/**
 * Case drill-down (painel §8). Renders the event timeline for a single CPF
 * within one workspace. Pattern B — full PII visible.
 */
export function CaseDrilldownDialog({ workspaceId, beneficiarioTaxPayerId, onClose }: IProps) {
  const open = !!beneficiarioTaxPayerId

  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'case-drilldown', workspaceId, beneficiarioTaxPayerId],
    queryFn: () => governanceApi.caseDrilldown(workspaceId, beneficiarioTaxPayerId!),
    enabled: open && !!workspaceId,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Drill-down {beneficiarioTaxPayerId ? `— CPF ${beneficiarioTaxPayerId}` : ''}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar eventos.</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quando</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detalhe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.data.events.map((ev, i) => (
                <TableRow key={`${ev.event_type}-${ev.event_at}-${i}`}>
                  <TableCell>{ev.event_at ? dayjs(ev.event_at).format('DD/MM/YYYY HH:mm') : '—'}</TableCell>
                  <TableCell>{ev.event_type}{ev.movement_type ? ` / ${ev.movement_type}` : ''}</TableCell>
                  <TableCell>{ev.plan ?? '—'}</TableCell>
                  <TableCell>{ev.status ?? '—'}</TableCell>
                  <TableCell>{ev.detail ?? '—'}</TableCell>
                </TableRow>
              ))}
              {data.data.events.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">Nenhum evento para este CPF.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
